import moment from 'moment';
import type { TimeRange } from '../span-timeline/timeAxis';

/**
 * The spans a reader can put on screen.
 *
 * A list rather than a pair of draggable handles: the question is nearly always "the last N of
 * something", and picking that from a list is one act where dragging two handles to the same
 * answer is several. The handles are left to the one thing only they can express — a span with
 * no relation to now — which is what `Custom` is for.
 */
export type TimeRangePreset = {
    value: string;
    label: string;
    amount: number;
    unit: 'days' | 'months' | 'years';
};

export const TIME_RANGE_PRESETS: TimeRangePreset[] = [
    { value: '7d', label: 'Past 7 days', amount: 7, unit: 'days' },
    { value: '30d', label: 'Past 30 days', amount: 30, unit: 'days' },
    { value: '3m', label: 'Past 3 months', amount: 3, unit: 'months' },
    { value: '6m', label: 'Past 6 months', amount: 6, unit: 'months' },
    { value: '1y', label: 'Past year', amount: 1, unit: 'years' },
    { value: '2y', label: 'Past 2 years', amount: 2, unit: 'years' },
    { value: '5y', label: 'Past 5 years', amount: 5, unit: 'years' },
    { value: '10y', label: 'Past 10 years', amount: 10, unit: 'years' },
];

/** Two dates the reader picked. Not a span, so it has none to compute. */
export const CUSTOM_RANGE = 'custom';

/** Long enough to hold several versions of a monthly dataset, which is what we publish. */
export const DEFAULT_RANGE = '6m';

export const rangeForPreset = (value: string, now: Date): TimeRange | null => {
    const preset = TIME_RANGE_PRESETS.find(candidate => candidate.value === value);
    if (!preset) {
        return null;
    }

    // Calendar months and years, so "past 6 months" lands on the same day of the month rather
    // than a count of days that drifts through February.
    return { from: moment.utc(now).subtract(preset.amount, preset.unit).toDate(), to: now };
};
